<template>
  <div class="wme" data-test="wa-message-editor">
    <!-- A NEW message of the office's own starts with its name; staff see this name when they pick the message. -->
    <template v-if="create">
      <label class="wme-label" :for="inputId + '-name'">Name of your message</label>
      <input
        :id="inputId + '-name'"
        ref="name"
        v-model="name"
        type="text"
        class="wme-name"
        autocomplete="off"
        placeholder="For example: Late fee reminder"
        :disabled="submitting"
        data-test="wa-custom-name"
      >
      <p v-if="verdict.nameError" class="wme-name-error" role="alert" data-test="wa-custom-name-error">{{ verdict.nameError }}</p>
      <p v-else-if="titlePreview" class="wme-name-hint" data-test="wa-custom-name-hint">
        Your staff will see it listed as &ldquo;{{ titlePreview }}&rdquo;.
      </p>
    </template>

    <label class="wme-label" :class="{ 'wme-label--spaced': create }" :for="inputId">{{ create ? 'What it says' : 'Your new wording' }}</label>
    <textarea
      :id="inputId"
      ref="input"
      v-model="draft"
      class="wme-input"
      rows="6"
      :disabled="submitting"
      :aria-describedby="inputId + '-help'"
      data-test="wa-edit-input"
    />

    <div class="wme-blanks" data-test="wa-edit-blanks">
      <span :id="inputId + '-help'" class="wme-blanks__label">Add a blank that fills in by itself:</span>
      <button
        v-for="label in blankLabels"
        :key="label"
        type="button"
        class="wme-blank-btn"
        :disabled="submitting"
        data-test="wa-edit-blank"
        @click="addBlank(label)"
      >{{ label }}</button>
    </div>

    <ul v-if="verdict.errors.length" class="wme-list wme-list--error" data-test="wa-edit-errors">
      <li v-for="e in verdict.errors" :key="e.code">{{ e.message }}</li>
    </ul>

    <ul v-if="verdict.warnings.length" class="wme-list wme-list--warning" data-test="wa-edit-warnings">
      <li v-for="w in verdict.warnings" :key="w.code">{{ w.message }}</li>
    </ul>

    <div v-if="preview" class="wme-preview" data-test="wa-edit-preview">
      <div class="wme-preview__label">What patients will see</div>
      <div class="wme-preview__body"><WhatsAppMessageText :text="preview" /></div>
    </div>

    <p v-if="error" class="wme-server-error" role="alert" data-test="wa-edit-error">{{ error }}</p>

    <p v-if="create" class="wme-note" data-test="wa-edit-note">
      WhatsApp reviews every new message before it can be used. Once it&rsquo;s approved, your staff can
      send it from a conversation.
    </p>
    <p v-else-if="message.custom" class="wme-note" data-test="wa-edit-note">
      WhatsApp reviews every change before it can be used. Until it&rsquo;s approved, your staff
      keep sending your current message.
    </p>
    <p v-else class="wme-note" data-test="wa-edit-note">
      WhatsApp reviews every change before it goes out. Until it&rsquo;s approved, your patients
      keep getting your current message.
    </p>

    <div class="wme-actions">
      <button
        type="button"
        class="wme-submit"
        :disabled="!verdict.canSubmit || submitting"
        data-test="wa-edit-submit"
        @click="submit"
      >
        <b-spinner v-if="submitting" small class="mr-1" />
        {{ submitting ? 'Sending…' : 'Send for approval' }}
      </button>
      <button
        type="button"
        class="wme-cancel"
        :disabled="submitting"
        data-test="wa-edit-cancel"
        @click="$emit('cancel')"
      >Cancel</button>
    </div>
  </div>
</template>

<script>
/**
 * The inline editor for ONE WhatsApp message (Phase 2, 2026-10-10), shown inside a row of
 * WhatsAppMessagesCard.vue.
 *
 * Everything the practice sees while typing comes from the REAL rules: `editVerdict` runs the same
 * validator the server runs (src/utils/whatsappTemplateRules.js, agreement-tested against the server's
 * copy), so an invalid message cannot be sent from here - the button stays off and the reason is on
 * screen. The server still checks every rule; if it refuses, its sentence is shown under the box.
 *
 * "What patients will see" is `previewWording`: this practice's own name and office phone and a
 * sample date, filled in the way the server fills the stored message. Nothing is ever rendered as
 * HTML - the text only goes through WhatsAppMessageText.
 *
 * It does not talk to the network. The card owns the save and passes `submitting` / `error` back in.
 */
import {
  BLANK_LABELS, CUSTOM_BLANK_LABELS, editVerdict, customVerdict, customTitlePreview, insertBlank, previewWording,
} from '@/utils/whatsappMessages';
import WhatsAppMessageText from './WhatsAppMessageText.vue';

let uid = 0;

export default {
  name: 'WhatsAppMessageEditor',

  components: { WhatsAppMessageText },

  props: {
    /**
     * The card's row: { key, wording, pendingEdit, sampleValues, custom }. For a NEW message (`create`) there is no
     * row yet: pass only `sampleValues` (this practice's own name and phone, for the preview).
     */
    message: { type: Object, required: true },
    /** A NEW message of the office's own: asks for a name too and emits { name, body }. */
    create: { type: Boolean, default: false },
    /** Only with `create`: start from this name and text (a message WhatsApp did not approve, to fix and send again). */
    initialName: { type: String, default: '' },
    initialBody: { type: String, default: '' },
    submitting: { type: Boolean, default: false },
    /** The sentence to show when the save was refused or failed; '' for none. */
    error: { type: String, default: '' },
  },

  data() {
    return {
      draft: this.startingText(),
      name: this.create ? this.initialName : '',
      inputId: `wme-input-${(uid += 1)}`,
    };
  },

  computed: {
    /** A message the office wrote itself may use only its practice name and office phone. */
    isCustom() {
      return this.create || this.message.custom === true;
    },
    blankLabels() {
      return this.isCustom ? CUSTOM_BLANK_LABELS : BLANK_LABELS;
    },
    verdict() {
      if (this.create) return customVerdict(this.name, this.draft);
      return editVerdict(this.draft, this.message.wording, this.message.key, { custom: this.isCustom });
    },
    titlePreview() {
      return customTitlePreview(this.name);
    },
    preview() {
      return previewWording(this.draft, this.message.sampleValues);
    },
  },

  mounted() {
    const first = this.create ? this.$refs.name : this.$refs.input;
    if (first && typeof first.focus === 'function') first.focus();
  },

  methods: {
    /**
     * A change WhatsApp did not approve opens with what the practice typed, so they adjust it rather than
     * start over; otherwise the message that is live now.
     */
    startingText() {
      if (this.create) return this.initialBody;
      const edit = this.message.pendingEdit;
      if (edit && edit.status === 'not_approved' && edit.wording) return edit.wording;
      return this.message.wording || '';
    },

    addBlank(label) {
      const el = this.$refs.input;
      const { text, caret } = insertBlank(this.draft, el && el.selectionStart, el && el.selectionEnd, label);
      this.draft = text;
      this.$nextTick(() => {
        if (!el) return;
        el.focus();
        if (typeof el.setSelectionRange === 'function') el.setSelectionRange(caret, caret);
      });
    },

    submit() {
      if (!this.verdict.canSubmit || this.submitting) return;
      if (this.create) this.$emit('submit', { name: this.name.trim(), body: this.draft });
      else this.$emit('submit', this.draft);
    },
  },
};
</script>

<style lang="scss" scoped>
@import './PatientPayments/tokens';

.wme {
  margin-top: 0.8rem;
}

.wme-label {
  display: block;
  margin-bottom: 0.25rem;
  color: $pp-text-muted;
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.02em;
}

.wme-label--spaced {
  margin-top: 0.8rem;
}

.wme-name {
  display: block;
  width: 100%;
  padding: 0.5rem 0.8rem;
  border: 1px solid $pp-border-strong;
  border-radius: $pp-radius-sm;
  background: $pp-surface;
  color: $pp-text-strong;
  /* px, not rem: the app root is 14px and iOS Safari zooms into any text field below 16px. */
  font-size: 16px;
  line-height: 1.5;

  &:focus {
    outline: none;
    border-color: $pp-brand;
    box-shadow: 0 0 0 3px $pp-brand-subtle;
  }
}

.wme-name-hint,
.wme-name-error {
  margin: 0.3rem 0 0;
  font-size: 0.83rem;
  line-height: 1.5;
  overflow-wrap: anywhere;
}

.wme-name-hint { color: $pp-text-muted; }
.wme-name-error { color: $pp-red-text; }

.wme-input {
  display: block;
  width: 100%;
  padding: 0.6rem 0.8rem;
  border: 1px solid $pp-border-strong;
  border-radius: $pp-radius-sm;
  background: $pp-surface;
  color: $pp-text-strong;
  /* px, not rem: the app root is 14px and iOS Safari zooms into any text field below 16px. */
  font-size: 16px;
  line-height: 1.5;
  resize: vertical;

  &:focus {
    outline: none;
    border-color: $pp-brand;
    box-shadow: 0 0 0 3px $pp-brand-subtle;
  }
}

.wme-blanks {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.4rem 0.5rem;
  margin-top: 0.6rem;
}

.wme-blanks__label {
  color: $pp-text-muted;
  font-size: 0.83rem;
}

.wme-blank-btn {
  padding: 0.25rem 0.7rem;
  border: 1px solid $pp-brand-border;
  border-radius: $pp-radius-pill;
  background: $pp-brand-subtle;
  color: $pp-brand-deep;
  font-size: 0.83rem;
  font-weight: 600;
  cursor: pointer;

  &:hover:not(:disabled) { border-color: $pp-brand; }
  &:disabled { opacity: 0.55; cursor: not-allowed; }
}

.wme-list {
  margin: 0.7rem 0 0;
  padding: 0.55rem 0.8rem 0.55rem 1.6rem;
  border: 1px solid;
  border-radius: $pp-radius-sm;
  font-size: 0.83rem;
  line-height: 1.5;
  overflow-wrap: anywhere;

  li + li { margin-top: 0.25rem; }
}

.wme-list--error {
  border-color: $pp-red-border;
  background: $pp-red-subtle;
  color: $pp-red-text;
}

.wme-list--warning {
  border-color: $pp-amber-border;
  background: $pp-amber-subtle;
  color: $pp-amber-text;
}

.wme-preview {
  margin-top: 0.8rem;
}

.wme-preview__label {
  margin-bottom: 0.25rem;
  color: $pp-text-muted;
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.02em;
}

.wme-preview__body {
  padding: 0.6rem 0.8rem;
  border-radius: $pp-radius-sm;
  background: $pp-surface-sunken;
  color: $pp-text-strong;
  font-size: 0.88rem;
  line-height: 1.6;
  overflow-wrap: anywhere;
}

.wme-server-error {
  margin: 0.8rem 0 0;
  padding: 0.55rem 0.8rem;
  border: 1px solid $pp-red-border;
  border-radius: $pp-radius-sm;
  background: $pp-red-subtle;
  color: $pp-red-text;
  font-size: 0.83rem;
  line-height: 1.5;
}

.wme-note {
  margin: 0.8rem 0 0;
  color: $pp-text-muted;
  font-size: 0.83rem;
  line-height: 1.5;
}

.wme-actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem 0.75rem;
  margin-top: 0.9rem;
}

.wme-submit {
  display: inline-flex;
  align-items: center;
  padding: 0.5rem 1.1rem;
  border: 1px solid $pp-brand;
  border-radius: $pp-radius-sm;
  background: $pp-brand;
  color: #fff;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;

  &:hover:not(:disabled) { background: $pp-brand-deep; border-color: $pp-brand-deep; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
}

.wme-cancel {
  padding: 0.5rem 0.9rem;
  border: 1px solid $pp-border-strong;
  border-radius: $pp-radius-sm;
  background: $pp-surface;
  color: $pp-text-strong;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;

  &:hover:not(:disabled) { border-color: $pp-text-muted; }
  &:disabled { opacity: 0.55; cursor: not-allowed; }
}
</style>
